import { createTheme } from "@mui/material/styles";
import { displayFontFamily, FontWeights, Palette, Effects } from "../constants.js";
import { RemSizes } from "../utils.js";
import { palette } from "./Palette/paletteTheme.js";
import { typography } from "./Typography/theme.js";
const MUISelect = {
  styleOverrides: {
    iconFilled: {
      right: RemSizes(20)
    },
    filled: {
      backgroundColor: "transparent"
    },
    root: {
      ".MuiInputBase-root": {
        cursor: "pointer",
        padding: "0 !important",
        "&:hover": {
          ".MuiSelect-iconFilled": {
            right: RemSizes(19)
          }
        },
        "&.Mui-focused": {
          padding: 0,
          ".MuiSelect-iconFilled": {
            right: RemSizes(19)
          },
          ".MuiInputBase-input": {
            paddingLeft: RemSizes(11)
          }
        },
        "&.Mui-disabled": {
          ".MuiSelect-iconFilled": {
            right: RemSizes(20)
          }
        },
        ".MuiInputBase-input": {
          "&:hover": {
            paddingLeft: RemSizes(11)
          }
        }
      },
      ".MuiFilledInput-root": {
        "&.Mui-focused": {
          ".MuiSelect-iconFilled": {
            color: `${palette.secondary.main}`
          }
        },
        "&.Mui-error": {
          ".MuiSelect-iconFilled": {
            color: `${palette.error.main}`
          }
        }
      }
    }
  }
};
const MuiButtonGroup = {
  styleOverrides: {
    root: {
      fontSize: "1rem",
      borderRadius: "25px"
    }
  }
};
const MUIButton = {
  styleOverrides: {
    root: ({
      ownerState: {
        fullWidth: fw,
        originalSize
      }
    }) => {
      const fullWidth = fw ? "100%" : void 0;
      const size = originalSize === "small" ? "auto" : "100%";
      const width = fullWidth || size;
      const maxWidth = fullWidth || RemSizes(358);
      return {
        width,
        maxWidth,
        textTransform: "none",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: RemSizes(100),
        boxShadow: "none",
        fontFamily: displayFontFamily,
        ":disabled": {
          color: `${palette.text.disabled}`
        },
        ":active": {
          position: "relative",
          boxShadow: "none"
        },
        // This styling is for a pseudo-element that makes up the inner border displayed
        // when a button is active(being pressed)
        ":active:before": {
          content: '" "',
          position: "absolute",
          top: "2px",
          left: "2px",
          right: "2px",
          bottom: "2px",
          background: "transparent",
          border: `1px solid ${palette.neutral.background}`,
          borderRadius: RemSizes(100)
        },
        ":focus-visible": {
          outline: "solid",
          outlineOffset: RemSizes(2),
          position: "relative"
        }
      };
    },
    sizeLarge: {
      fontSize: RemSizes(15),
      lineHeight: RemSizes(24),
      height: RemSizes(48),
      paddingTop: RemSizes(13),
      paddingRight: RemSizes(16),
      paddingBottom: RemSizes(13),
      paddingLeft: RemSizes(16),
      letterSpacing: RemSizes(0.25),
      fontWeight: FontWeights.MEDIUM
    },
    sizeMedium: {
      fontSize: RemSizes(14),
      lineHeight: RemSizes(21),
      height: RemSizes(38),
      paddingTop: RemSizes(8),
      paddingRight: RemSizes(12),
      paddingBottom: RemSizes(8),
      paddingLeft: RemSizes(12),
      letterSpacing: RemSizes(0.4),
      fontWeight: FontWeights.MEDIUM
    },
    sizeSmall: {
      fontSize: RemSizes(13),
      lineHeight: RemSizes(21),
      height: RemSizes(32),
      paddingTop: RemSizes(8),
      paddingRight: RemSizes(12),
      paddingBottom: RemSizes(8),
      paddingLeft: RemSizes(12),
      letterSpacing: RemSizes(0.4),
      fontWeight: FontWeights.MEDIUM
    },
    iconSizeLarge: {
      width: RemSizes(20),
      height: RemSizes(20),
      marginRight: RemSizes(8),
      marginTop: RemSizes(-2)
    },
    iconSizeMedium: {
      width: RemSizes(20),
      height: RemSizes(20),
      marginRight: RemSizes(8),
      marginTop: RemSizes(-2)
    },
    iconSizeSmall: {
      width: RemSizes(14),
      height: RemSizes(14),
      marginRight: RemSizes(8),
      marginTop: RemSizes(-4)
    },
    text: {
      disableRipple: true,
      transition: "none",
      "&:hover, &:focus": {
        textDecoration: "underline",
        backgroundColor: "transparent"
      },
      ":focus-visible": {
        outline: "none"
      }
    },
    containedPrimary: {
      // Primary styling
      // Prevent hover darkening
      ":hover, :focus-visible": {
        backgroundColor: palette.primary.main
      },
      ":focus-visible": {
        outlineColor: palette.primary.main
      }
    },
    containedWarning: {
      ":hover, :focus-visible": {
        backgroundColor: palette.warning.main
      },
      ":focus-visible": {
        outlineColor: palette.warning.main
      }
    },
    outlinedPrimary: {
      // Secondary styling
      color: palette.primary.main,
      borderColor: palette.primary.main,
      ":hover, :focus-visible": {
        backgroundColor: `${palette.primary.main}0D`
      },
      ":active": {
        borderColor: `transparent`,
        backgroundColor: `${palette.primary.main}0D`
      },
      ":active:before": {
        borderColor: palette.neutral.background
      }
    },
    containedError: {
      // Regular error styling
      backgroundColor: palette.error.main,
      ":hover, :focus-visible": {
        backgroundColor: palette.error.main
      },
      ":focus-visible": {
        outlineColor: palette.error.main
      }
    },
    outlinedError: {
      // Secondary error styling
      borderColor: palette.error.main,
      backgroundColor: "transparent",
      color: palette.error.main,
      ":hover, :focus-visible": {
        borderColor: palette.error.main,
        backgroundColor: `${palette.error.main}0D`
        /** Red, 4% Opacity */
      },
      ":active": {
        borderColor: `transparent`,
        backgroundColor: `${palette.error.main}14`
        /** Red, 5% Opacity */
      },
      ":active:before": {
        borderColor: palette.neutral.background
      }
    },
    outlinedWarning: {
      // Warning styling
      color: palette.warning.main,
      borderColor: palette.warning.main,
      ":hover, :focus-visible": {
        backgroundColor: `${palette.warning.main}0D`
      },
      ":active": {
        borderColor: `transparent`,
        backgroundColor: `${palette.warning.main}14`
      },
      ":active:before": {
        borderColor: palette.neutral.background
      }
    },
    // @ts-expect-error Type doesn't think this exists, its fine though
    "&.Mui-disabled": {
      backgroundColor: palette.neutral.light,
      color: palette.neutral.dark
    }
  }
};
const MUIFormControl = {
  styleOverrides: {
    root: {
      // Styling of variant="filled"
      // Note: This is the style that TextField uses by default
      ".MuiFilledInput-root": {
        borderRadius: "10px",
        backgroundColor: `${palette.neutral.background}`,
        border: `1px solid ${palette.neutral.dark}`,
        boxSizing: "border-box",
        minHeight: "58px",
        padding: "1px 16px 1px 1px",
        ".MuiInputAdornment-root": {
          marginLeft: ".75rem"
        },
        ".MuiFilledInput-input": {
          fontWeight: "500",
          fontSize: "0.938rem",
          lineHeight: "1.313rem",
          letterSpacing: "0.009rem",
          caretColor: palette.secondary.main
        },
        "&:hover, &.Mui-focused": {
          backgroundColor: `${palette.neutral.background}`,
          border: `1px solid ${palette.secondary.main}`,
          outline: `1px solid ${palette.secondary.main}`
        },
        "&.MuiFilledInput-underline": {
          "&:before, &:after": {
            borderBottom: "none"
          },
          "&:hover:before": {
            borderBottom: 0
          }
        },
        "&.Mui-disabled": {
          backgroundColor: palette.neutral.light,
          margin: "0px",
          "&:hover": {
            border: `1px solid ${palette.neutral.dark}`
          }
        },
        "&.Mui-error": {
          border: `1px solid ${palette.error.main}`,
          padding: "1px 16px 1px 1px",
          "&:hover, &.Mui-focused": {
            border: `1px solid ${palette.error.main}`,
            outline: `1px solid ${palette.error.main}`,
            padding: "0px 15px 0px 0px"
          }
        }
      },
      // Styling of variant="outlined"
      ".MuiOutlinedInput-root": {
        borderRadius: "10px"
      },
      ".MuiInputBase-multiline": {
        minHeight: "98px",
        alignItems: "start",
        ".MuiInputBase-input": {
          padding: "12px 0px 12px 12px",
          marginTop: "10px"
        }
      },
      ".MuiFormHelperText-root": {
        fontSize: "0.75rem",
        lineHeight: "1.063rem",
        letterSpacing: "0.025rem",
        fontWeight: 500,
        color: palette.neutral.dark,
        "&.Mui-error": {
          color: palette.error.main
        }
      },
      ".MuiIconButton-root": {
        marginRight: "-8px",
        color: palette.neutral.dark
      }
    }
  }
};
const MUITextField = {
  styleOverrides: {
    root: {
      backgroundColor: "transparent",
      color: palette.text.primary,
      "label.MuiInputLabel-root": {
        fontWeight: "500",
        fontSize: "0.938rem",
        lineHeight: "1.313rem",
        letterSpacing: "0.009rem",
        padding: "4px 0px 0px 2px"
      },
      ".MuiInputLabel-root": {
        color: palette.neutral.dark,
        "&.Mui-focused": {
          color: palette.secondary.main
        },
        "&.Mui-error": {
          color: palette.error.main
        }
      }
    }
  }
};
const MUICheckbox = {
  styleOverrides: {
    root: {
      color: Palette.DARK_GRAY,
      "&.Mui-checked": {
        color: palette.secondary.main
      },
      "&.Mui-disabled": {
        color: palette.neutral.main
      }
    }
  }
};
const MUISwitch = {
  styleOverrides: {
    switchBase: {
      // Controls default (unchecked) color for the thumb
      color: palette.neutral.dark,
      // Controls checked color for the thumb
      "&.Mui-checked": {
        color: palette.secondary.main
      },
      "&.Mui-disabled": {
        color: palette.neutral.main
      },
      // Controls disabled color for the track
      "&.Mui-disabled+.MuiSwitch-track": {
        opacity: 1,
        backgroundColor: palette.neutral.backgroundUnchecked
      },
      // Controls checked color for the track
      "&.Mui-checked+.MuiSwitch-track": {
        opacity: 1,
        backgroundColor: palette.secondary.backgroundAlternate
      }
    },
    track: {
      // Controls default (unchecked) color for the track
      backgroundColor: palette.neutral.backgroundUnchecked,
      opacity: 1
    },
    thumb: {
      // Controls default (unchecked) color for the thumb
      boxShadow: Effects.DROP_SHADOW,
      "&.Mui-checked": {
        color: palette.secondary.main
      }
    }
  }
};
const MUILink = {
  styleOverrides: {
    root: {
      fontFamily: displayFontFamily,
      color: palette.secondary.main,
      textDecorationColor: palette.secondary.main
    }
  }
};
const MuiTab = {
  styleOverrides: {
    root: ({
      theme: theme2
    }) => ({
      color: Palette.DARK_GRAY,
      textTransform: "none",
      opacity: 1,
      [theme2.breakpoints.down("sm")]: {
        fontSize: theme2.typography.subtitle2.fontSize,
        lineHeight: theme2.typography.subtitle2.fontSize,
        padding: theme2.spacing(2)
      },
      [theme2.breakpoints.up("sm")]: {
        fontSize: theme2.typography.body1.fontSize,
        lineHeight: theme2.typography.body1.fontSize,
        padding: theme2.spacing(2.5)
      }
    })
  }
};
const MUITabs = {
  defaultProps: {
    textColor: "secondary",
    indicatorColor: "secondary"
  },
  styleOverrides: {
    root: {
      // Creates the line under the Tabs
      "div.MuiTabs-flexContainer": {
        borderBottom: `1px solid ${Palette.LIGHT_GRAY}`
      },
      // Color the selected tab purple
      ".Mui-selected": {
        color: Palette.VIOLET
      }
    },
    indicator: {
      height: "1px !important"
    }
  }
};
const MUIPagination = {
  defaultProps: {
    color: "primary",
    size: "large"
  },
  styleOverrides: {
    ul: {
      width: "fit-content"
    },
    root: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center"
    }
  }
};
const MuiPaginationItem = {
  defaultProps: {
    sx: {
      typography: "Subhead"
    },
    color: "primary",
    size: "large"
  },
  styleOverrides: {
    root: ({
      theme: theme2
    }) => ({
      ellipsis: {
        fontSize: theme2.typography.subtitle2.fontSize,
        lineHeight: theme2.typography.subtitle2.lineHeight,
        letterSpacing: theme2.typography.subtitle2.letterSpacing
      },
      textPrimary: {
        "&.MuiPaginationItem-page, &.MuiPaginationItem-ellipsis": {
          color: Palette.DARK_GRAY
        },
        "&.Mui-disabled.MuiPaginationItem-previousNext": {
          color: Palette.GRAY,
          opacity: 1
        },
        "&.MuiPaginationItem-previousNext": {
          color: Palette.VIOLET
        },
        "&.MuiPaginationItem-page.Mui-selected, &.MuiPaginationItem-page:hover, &.MuiPaginationItem-previousNext:hover": {
          color: Palette.VIOLET,
          backgroundColor: Palette.VIOLET_LIGHTER_TRANSPARENT
        }
      },
      text: {
        "&.MuiPaginationItem-page, &.MuiPaginationItem-previousNext, &.MuiPaginationItem-ellipsis": {
          margin: "0 2px"
        }
      }
    })
  }
};
const MUIAlert = {
  styleOverrides: {
    root: {
      backgroundColor: palette.neutral.background,
      border: `1px solid ${palette.neutral.light}`,
      padding: "16px"
    },
    message: {
      width: "100%",
      padding: "4px 0px 0px 0px"
    },
    icon: {
      padding: "0px",
      marginTop: "4px"
    }
  }
};
const MUIDialog = {
  styleOverrides: {
    paper: {
      borderRadius: "10px",
      padding: "24px",
      boxShadow: Effects.DROP_SHADOW_OVERLAY
    }
  },
  defaultProps: {
    maxWidth: "xs"
  }
};
const MUIDialogContent = {
  styleOverrides: {
    root: {
      padding: "12px 0px"
    }
  }
};
const MUIRadio = {
  styleOverrides: {
    root: {
      color: Palette.DARK_GRAY,
      "&.Mui-checked": {
        color: palette.secondary.main
      },
      "&.Mui-disabled": {
        color: palette.neutral.main
      }
    }
  }
};
const MUIMenu = {
  styleOverrides: {
    paper: {
      borderRadius: RemSizes(10),
      marginTop: RemSizes(6),
      boxShadow: Effects.DROP_SHADOW_OVERLAY,
      backgroundColor: Palette.WHITE
    }
  }
};
const MUIMenuItem = {
  styleOverrides: {
    root: {
      "&.MuiButtonBase-root": {
        padding: `${RemSizes(12)} ${RemSizes(24)}`,
        fontSize: RemSizes(15),
        fontWeight: FontWeights.MEDIUM,
        lineHeight: RemSizes(21),
        letterSpacing: RemSizes(0.15),
        minHeight: "auto",
        backgroundColor: "transparent",
        "&.Mui-selected": {
          backgroundColor: Palette.LIGHT_GRAY
        },
        "&:hover": {
          backgroundColor: Palette.BACKGROUND
        }
      }
    }
  }
};
const MuiContainer = {
  styleOverrides: {
    maxWidthSm: {
      "@media (min-width: 600px)": {
        maxWidth: "440px"
      }
    }
  }
};
const MuiCard = {
  defaultProps: {
    variant: "outlined"
  },
  styleOverrides: {
    root: ({
      ownerState,
      theme: theme2
    }) => {
      var _a, _b, _c, _d;
      return {
        ////////////////////////
        // BEGIN OUTLINED STYLES
        ////////////////////////
        ...!!ownerState.color && ownerState.variant === "outlined" && {
          border: "none",
          boxShadow: `inset 0 0 1px 1px ${((_a = theme2.palette[ownerState.color]) == null ? void 0 : _a.border) || theme2.palette[ownerState.color].main}`
        },
        //////////////////////
        // END OUTLINED STYLES
        //////////////////////
        ////////////////////////
        // BEGIN CONTAINED STYLES
        ////////////////////////
        ...ownerState.variant === "contained" && {
          border: `1px solid ${((_b = theme2.palette[ownerState.color]) == null ? void 0 : _b.border) || ((_c = theme2.palette[ownerState.color]) == null ? void 0 : _c.main) || theme2.palette.divider}`,
          backgroundColor: (_d = theme2.palette[ownerState.color]) == null ? void 0 : _d.background
        }
        //////////////////////
        // END CONTAINED STYLES
        //////////////////////
      };
    }
  }
};
const MuiCardContent = {
  styleOverrides: {
    root: ({
      theme: theme2
    }) => {
      return {
        "&:last-child": {
          paddingBottom: `${theme2.spacing(2)}`
        }
      };
    }
  }
};
const MuiPaper = {
  styleOverrides: {
    root: {
      borderRadius: "10px"
    }
  }
};
const MuiStepper = {
  styleOverrides: {
    root: ({
      theme: theme2
    }) => ({
      ".MuiStepIcon-root": {
        color: Palette.BACKGROUND,
        width: "27px",
        height: "27px",
        "&.Mui-active": {
          color: theme2.palette.secondary.background,
          border: `1px solid ${theme2.palette.secondary.main}`,
          borderRadius: "27px",
          ".MuiStepIcon-text": {
            fill: theme2.palette.secondary.main
          }
        },
        ".MuiStepIcon-text": {
          fill: theme2.palette.neutral.main
        }
      },
      ".MuiStepConnector-lineVertical": {
        minHeight: "16px"
      },
      ".MuiStepLabel-iconContainer": {
        paddingRight: theme2.spacing(1.5)
      }
    })
  }
};
const MuiAccordion = {
  styleOverrides: {
    root: ({
      theme: theme2
    }) => ({
      ".MuiAccordionSummary-root": {
        minHeight: "unset"
      },
      ".MuiAccordionSummary-content": {
        margin: `${theme2.spacing(2)} 0`
      }
    })
  }
};
const MuiChip = {
  defaultProps: {
    color: "primary"
  },
  styleOverrides: {
    root: ({
      ownerState
    }) => {
      var _a, _b;
      if (ownerState.color === "primary") {
        return {
          background: palette.secondary.main,
          color: palette.secondary.contrastText
        };
      }
      if (ownerState.color === "secondary") {
        return {
          background: palette.neutral.backgroundUnchecked,
          color: palette.neutral.dark
        };
      }
      return {
        background: (_a = palette[ownerState.color]) == null ? void 0 : _a.background,
        color: (_b = palette[ownerState.color]) == null ? void 0 : _b.main
      };
    }
  }
};
const MuiAutocomplete = {
  styleOverrides: {
    root: {
      "&.MuiAutocomplete-multiple": {
        "&, &:hover": {
          ".MuiFilledInput-root, .MuiFilledInput-root.Mui-focused": {
            padding: "22px 0 0 7px"
          }
        }
      },
      "&:not(&.MuiAutocomplete-multiple)": {
        "&, &:hover": {
          ".MuiFilledInput-root, .Mui-focused.MuiFilledInput-root": {
            ".MuiFilledInput-input": {
              padding: "22px 0 0 11px"
            }
          }
        }
      }
    }
  }
};
const components = {
  MuiTypography: {
    defaultProps: {
      fontFamily: displayFontFamily
    }
  },
  MuiAccordion,
  MuiAutocomplete,
  MuiButton: MUIButton,
  MuiButtonGroup,
  MuiCard,
  MuiCardContent,
  MuiCheckbox: MUICheckbox,
  MuiChip,
  MuiContainer,
  MuiFormControl: MUIFormControl,
  MuiLink: MUILink,
  MuiPaper,
  MuiSwitch: MUISwitch,
  MuiTextField: MUITextField,
  MuiTab,
  MuiTabs: MUITabs,
  MuiPagination: MUIPagination,
  MuiPaginationItem,
  MuiDialog: MUIDialog,
  MuiDialogContent: MUIDialogContent,
  MuiAlert: MUIAlert,
  MuiRadio: MUIRadio,
  MuiSelect: MUISelect,
  MuiStepper,
  MuiMenu: MUIMenu,
  MuiMenuItem: MUIMenuItem
};
const theme = createTheme({
  palette,
  components
});
theme.typography = {
  pxToRem: RemSizes,
  fontFamily: displayFontFamily,
  fontSize: 14,
  fontWeightBold: FontWeights.BOLD,
  fontWeightLight: FontWeights.LIGHT,
  fontWeightMedium: FontWeights.MEDIUM,
  fontWeightRegular: FontWeights.NORMAL,
  htmlFontSize: 16,
  h1: {
    fontSize: "3.188rem",
    lineHeight: "4.0rem",
    letterSpacing: "0",
    fontWeight: FontWeights.BOLD,
    [theme.breakpoints.down("sm")]: {
      fontSize: "2.25rem",
      lineHeight: "2.813rem",
      letterSpacing: "0"
    }
  },
  h2: {
    fontSize: "2.25rem",
    lineHeight: "2.813rem",
    letterSpacing: "0",
    fontWeight: FontWeights.BOLD,
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.625rem",
      lineHeight: "2.188rem",
      letterSpacing: "0"
    }
  },
  h3: {
    fontSize: "1.625rem",
    lineHeight: "2.188rem",
    letterSpacing: "0",
    fontWeight: FontWeights.BOLD,
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.313rem",
      lineHeight: "1.75rem",
      letterSpacing: "0.006rem"
    }
  },
  h4: {
    fontSize: "1.313rem",
    lineHeight: "1.75rem",
    letterSpacing: "0.006rem",
    fontWeight: FontWeights.BOLD,
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.188rem",
      lineHeight: "1.625rem",
      letterSpacing: "0.006rem"
    }
  },
  h5: {
    fontSize: "1.188rem",
    lineHeight: "1.625rem",
    letterSpacing: "0.006rem",
    fontWeight: FontWeights.BOLD,
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.063rem",
      lineHeight: "1.438rem",
      letterSpacing: "0.006rem"
    }
  },
  h6: {
    fontSize: "1.063rem",
    lineHeight: "1.438rem",
    letterSpacing: "0.006rem",
    fontWeight: FontWeights.BOLD
  },
  subtitle1: {
    fontSize: "1.063rem",
    lineHeight: "1.483rem",
    letterSpacing: "0.013rem",
    fontWeight: FontWeights.NORMAL
  },
  subtitle2: {
    fontSize: "0.938rem",
    lineHeight: "1.313rem",
    letterSpacing: "0.009rem",
    fontWeight: FontWeights.NORMAL
  },
  body1: {
    fontSize: "1.063rem",
    lineHeight: "1.438rem",
    letterSpacing: "0.013rem",
    fontWeight: FontWeights.NORMAL,
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.938rem",
      lineHeight: "1.313rem",
      letterSpacing: "0.009rem"
    }
  },
  body2: {
    fontSize: "0.938rem",
    lineHeight: "1.313rem",
    letterSpacing: "0.009rem",
    fontWeight: FontWeights.NORMAL,
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.813rem",
      lineHeight: "1.063rem",
      letterSpacing: "0.025rem"
    }
  },
  caption: {
    fontSize: "0.813rem",
    lineHeight: "1.063rem",
    letterSpacing: "0.025rem",
    fontWeight: FontWeights.NORMAL
  },
  caption2: {
    fontSize: "0.75rem",
    lineHeight: "1.063rem",
    letterSpacing: "0.025rem",
    fontWeight: FontWeights.NORMAL
  },
  overline: {
    fontSize: "0.688rem",
    lineHeight: "1.063rem",
    letterSpacing: "0.013rem",
    fontWeight: FontWeights.NORMAL
  },
  button: {
    fontFamily: displayFontFamily,
    fontSize: "0.938rem",
    letterSpacing: "0.009rem",
    lineHeight: "1.313rem",
    fontWeight: FontWeights.MEDIUM
  },
  /** DEPRECATED */
  ...typography
};
export {
  theme
};
//# sourceMappingURL=theme.js.map
